import React, { useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Settings } from './pages/Settings';
import { SpacesExplorer } from './pages/SpacesExplorer';
import { Analytics } from './pages/Analytics';
import { Navigation } from './components/Navigation';
import { ContentShell } from './components/ContentShell';
import { UserContextProvider } from './lib/userContext';
import { ChatProvider } from './lib/chatContext';
import { ActivityFeedProvider } from './lib/activityFeed';
import { KnowledgeSpaceModal } from './components/KnowledgeSpaceModal';
import { PartnerView } from './components/PartnerView';
import { DemoController } from './components/DemoController';
import { DemoOverlay } from './components/DemoOverlay';
import { ActivationOverlay } from './components/ActivationOverlay';
import { ErrorBoundary } from './components/ErrorBoundary';
import { seedData } from './data/seedData';
import { People } from './pages/People';
import { Orders } from './pages/Orders';
import { AccountOverview } from './pages/AccountOverview';
import { OmnipresentChat } from './components/OmnipresentChat';
import { DotGridBackground } from './components/DotGridBackground';
import { CommandPalette } from './components/CommandPalette';
export function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [currentView, setCurrentView] = useState('accounts');
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedBotType, setSelectedBotType] = useState<'internal' | 'external'>('external');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  // Scroll to top when view changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentView]);
  useEffect(() => {
    const handleNavigation = (e: CustomEvent) => {
      const {
        view,
        metadata
      } = e.detail;
      setCurrentView(view);
      if (metadata?.accountId) {
        setSelectedAccountId(metadata.accountId);
      } else {
        setSelectedAccountId(null);
      }
      setSelectedSpaceId(null);
    };
    window.addEventListener('quad-navigate', handleNavigation as EventListener);
    return () => {
      window.removeEventListener('quad-navigate', handleNavigation as EventListener);
    };
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accountId = params.get('accountId');
    const bot = params.get('bot');
    if (accountId && seedData.accounts.find(a => a.id === accountId)) {
      setIsLoggedIn(true);
      setSelectedAccountId(accountId);
      if (bot === 'internal' || bot === 'external') {
        setSelectedBotType(bot);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  // Add Cmd+K handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const handleStartActivation = () => {
    setIsActivating(true);
  };
  const handleActivationComplete = () => {
    setIsActivating(false);
    setIsLoggedIn(true);
  };
  const selectedAccount = selectedAccountId ? seedData.accounts.find(a => a.id === selectedAccountId) || null : null;
  const handleOpenPartnerView = (accountId: string, botType?: 'internal' | 'external') => {
    console.log('[App] handleOpenPartnerView called');
    console.log('[App] Received accountId:', accountId);
    console.log('[App] Received botType:', botType);
    console.log('[App] Current selectedAccountId:', selectedAccountId);
    console.log('[App] Setting selectedAccountId to:', accountId);
    setSelectedAccountId(accountId);
    if (botType) {
      console.log('[App] Setting selectedBotType to:', botType);
      setSelectedBotType(botType);
    }
    console.log('[App] State update complete');
  };
  const handleNavigate = (view: string, metadata?: any) => {
    setCurrentView(view);
    if (metadata?.accountId) {
      setSelectedAccountId(metadata.accountId);
    }
  };
  const handleNavigateToSection = (section: string) => {
    setCurrentView(section);
  };
  if (!isLoggedIn && !isActivating) {
    return <Login onLogin={handleStartActivation} data-id="element-0" />;
  }
  if (isActivating) {
    return <ActivationOverlay onComplete={handleActivationComplete} data-id="element-1" />;
  }
  return <ErrorBoundary data-id="element-2">
      <UserContextProvider data-id="element-3">
        <ActivityFeedProvider data-id="element-4">
          <ChatProvider data-id="element-5">
            <div className="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative" data-app-shell data-id="element-6">
              {/* Dot Grid Background */}
              <DotGridBackground data-id="element-7" />
              <Navigation currentView={currentView} onViewChange={setCurrentView} onSelectAccount={setSelectedAccountId} onSelectSpace={setSelectedSpaceId} data-id="element-8" />
              {/* Main content area - NO MARGIN, full width */}
              <div data-route={currentView} data-id="element-9">
                {currentView === 'accounts' && <ContentShell data-id="element-10">
                    {selectedAccountId ? <>
                        {console.log('[App] Rendering AccountOverview')}
                        {console.log('[App] selectedAccountId:', selectedAccountId)}
                        {console.log('[App] currentView:', currentView)}
                        <AccountOverview accountId={selectedAccountId} onNavigate={handleNavigateToSection} data-id="element-11" />
                      </> : <>
                        {console.log('[App] Rendering Home')}
                        <Home onOpenPartnerView={(id, botType) => {
                    console.log('[App] Home onOpenPartnerView called');
                    console.log('[App] id:', id);
                    console.log('[App] botType:', botType);
                    handleOpenPartnerView(id, botType);
                  }} data-id="element-12" />
                      </>}
                  </ContentShell>}
                {currentView === 'spaces' && <ContentShell data-id="element-13">
                    <SpacesExplorer onSelectSpace={setSelectedSpaceId} data-id="element-14" />
                  </ContentShell>}
                {currentView === 'analytics' && <ContentShell data-id="element-15">
                    <Analytics data-id="element-16" />
                  </ContentShell>}
                {currentView === 'settings' && <ContentShell data-id="element-17">
                    <Settings data-id="element-18" />
                  </ContentShell>}
                {currentView === 'people' && <ContentShell data-id="element-19">
                    <People data-id="element-20" />
                  </ContentShell>}
                {currentView === 'orders' && <ContentShell data-id="element-21">
                    <Orders data-id="element-22" />
                  </ContentShell>}
                {currentView === 'assets' && <ContentShell data-id="element-23">
                    <div className="text-center py-12" data-id="element-24">
                      <p className="text-slate-400" data-id="element-25">Assets view coming soon</p>
                    </div>
                  </ContentShell>}
              </div>
              {/* Command Palette */}
              <AnimatePresence data-id="element-26">
                {showCommandPalette && <CommandPalette onNavigate={handleNavigate} onClose={() => setShowCommandPalette(false)} data-id="element-27" />}
              </AnimatePresence>
              {/* Omnipresent Chat - Floating overlay, doesn't affect layout */}
              <OmnipresentChat currentAccountId={selectedAccountId} currentView={currentView} onNavigate={handleNavigate} onAction={(action, metadata) => {
              console.log('Chat action:', action, metadata);
            }} data-id="element-28" />
              {selectedSpaceId && <KnowledgeSpaceModal spaceId={selectedSpaceId} onClose={() => setSelectedSpaceId(null)} data-id="element-29" />}
              {selectedAccount && <PartnerView account={selectedAccount} onClose={() => setSelectedAccountId(null)} initialBotType={selectedBotType} data-id="element-30" />}
              <DemoController data-id="element-31" />
              <DemoOverlay data-id="element-32" />
              <Toaster position="top-right" closeButton data-id="element-33" />
            </div>
          </ChatProvider>
        </ActivityFeedProvider>
      </UserContextProvider>
    </ErrorBoundary>;
}